import { Button } from '@askable/ui/core/button';
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from '@askable/ui/core/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@askable/ui/core/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@askable/ui/core/tooltip';
import { cn } from '@askable/ui/lib/utils';
import { X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'shared-utils';

type ResponsiveMenuProps = {
  id: string;
  trigger: ReactNode;
  children: ReactNode;
  title?: string;
  align?: 'start' | 'center' | 'end'; // DropdownMenu-specific positioning
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  contentClassName?: string;
  isCollapsed?: boolean; // DropdownMenu-specific behaviour
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const ResponsiveMenu = ({
  id,
  trigger,
  children,
  title,
  align = 'start',
  side = 'right',
  sideOffset = 12,
  contentClassName,
  isCollapsed,
  isOpen,
  onOpenChange,
}: ResponsiveMenuProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useMediaQuery('(max-width: 639px)');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (isMounted && isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          hideCloseButton
          className="!top-4 max-w-[98vw] !translate-y-0 gap-2 rounded-lg p-0 data-[state=closed]:!slide-out-to-top-0 data-[state=open]:!slide-in-from-top-0"
        >
          <div className="flex items-center justify-between pl-3 pr-2 pt-2">
            {title ? <DialogTitle className="text-sm">{title}</DialogTitle> : <div />}
            <DialogClose asChild>
              <Button variant="ghost" size="icon">
                <X className="size-4" />
              </Button>
            </DialogClose>
          </div>
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      {title && isCollapsed ? (
        <Tooltip
          open={isTooltipOpen}
          onOpenChange={open => {
            setIsTooltipOpen(open && isHoveredRef.current);
          }}
        >
          <TooltipTrigger
            asChild
            onPointerEnter={() => {
              isHoveredRef.current = true;
            }}
            onPointerLeave={() => {
              isHoveredRef.current = false;
              setIsTooltipOpen(false);
            }}
          >
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            {title}
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent
        data-testid={id}
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn('w-96', contentClassName)}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
